
import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

interface FeedbackChartsProps {
  categoryData: { name: string; count: number }[];
  ratingDistribution: { rating: number; count: number }[];
  monthlyData: { month: string; count: number; avgRating: number }[];
}

const FeedbackCharts: React.FC<FeedbackChartsProps> = ({ 
  categoryData, 
  ratingDistribution, 
  monthlyData 
}) => {
  // Updated colors to match NWSISD brand
  const COLORS = ['#004161', '#cfb9a2', '#0f8f8c', '#b1c3cf', '#f1eceb'];
  const RATING_COLORS = ['#b1c3cf', '#cfb9a2', '#0f8f8c', '#004161'];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card className="border-accent-light overflow-hidden">
        <CardHeader className="bg-primary/5">
          <CardTitle className="font-heading text-primary">Feedback by Category</CardTitle>
          <CardDescription className="font-body text-gray-600">
            Distribution of feedback across course categories
          </CardDescription>
        </CardHeader>
        <CardContent className="h-72 pt-4">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={categoryData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="count"
              >
                {categoryData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(value) => [`${value} feedback submissions`, 'Count']} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
      
      <Card className="border-accent-light overflow-hidden">
        <CardHeader className="bg-primary/5">
          <CardTitle className="font-heading text-primary">Rating Distribution</CardTitle>
          <CardDescription className="font-body text-gray-600">
            Breakdown of feedback by rating scores
          </CardDescription>
        </CardHeader>
        <CardContent className="h-72 pt-4">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={ratingDistribution}
              margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#f1eceb" />
              <XAxis dataKey="rating" />
              <YAxis />
              <Tooltip formatter={(value) => [`${value} ratings`, 'Count']} />
              <Bar dataKey="count" name="Number of Ratings">
                {ratingDistribution.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={RATING_COLORS[entry.rating - 1]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
      
      <Card className="lg:col-span-2 border-accent-light overflow-hidden">
        <CardHeader className="bg-primary/5">
          <CardTitle className="font-heading text-primary">Monthly Feedback Trends</CardTitle>
          <CardDescription className="font-body text-gray-600">
            Feedback submissions and ratings over time
          </CardDescription>
        </CardHeader>
        <CardContent className="h-80 pt-4">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={monthlyData}
              margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#f1eceb" />
              <XAxis dataKey="month" />
              <YAxis yAxisId="left" orientation="left" />
              <YAxis yAxisId="right" orientation="right" domain={[0, 4]} />
              <Tooltip />
              <Legend />
              <Bar yAxisId="left" dataKey="count" name="Feedback Count" fill="#004161" />
              <Bar yAxisId="right" dataKey="avgRating" name="Avg. Rating" fill="#0f8f8c" />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </div>
  );
};

export default FeedbackCharts;
